/**
 * Citibox brand palette tokens (design-language: Electric Purple), mirrored as
 * named tokens for direct reference (e.g. the /ui-kit showcase). The theme in
 * ./index.ts consumes these so the named tokens and the live theme never drift.
 * See docs/DESIGN.md §2.
 */
export const citiboxColors = {
  primary: '#7942ED', // Electric Purple — primary actions
  primaryDark: '#552EA6', // Deep Purple — pressed states
  primaryLight: '#B294F3', // Soft Lavender — highlights
  // Pale brand fill for the container of a value that must be found first (the lock-code
  // «siguiente» slot). A surface, not an action: never used for a control's background, and
  // light enough that `primary` and `textPrimary` both clear contrast on it.
  primarySurface: '#F1EAFE',
  secondary: '#0ECECF', // Vibrant Teal — decorative accents
  secondaryDark: '#0BAFAF',
  success: '#2ED25C',
  // The deep tone a filled success notice is painted in, under white text. Named rather than left
  // to MUI's derivation (darken 0.3 → rgb(32,147,64)), which gives white text 3.96:1 — below AA for
  // the 14px bold message. This is MUI's own darkening of the brand green at 0.35, the lightest
  // step on that line that clears 4.5:1 (≈4.53). Also the label tone of every filled success chip.
  successDark: '#1D883B',
  warning: '#F0AA22',
  // The deep tone a warning LABEL is written in. Named rather than left to MUI's derivation
  // (darken 0.3 → rgb(168,118,23)), which reads 3.57:1 on the chip's own 16% warning tint — below AA for a
  // 13px label. This is the first darkening that clears 4.5:1 on that tint (≈4.59) and on white
  // (≈5.13). Shared by every warning chip, the degraded market pill and the filled warning Alert included.
  warningDark: '#906614',
  error: '#F26279',
  info: '#2775FF',
  textPrimary: '#50495E', // Dark Purple-Gray — headings/body (never pure black)
  textSecondary: '#ACA5BB', // Soft Lavender-Gray — secondary/placeholder
  surface: '#EEEEEF', // Light Gray-Lavender — input/section fills
  background: '#FFFFFF',
  divider: 'rgba(69, 64, 79, 0.12)', // tonal hairline (Figma 67:953)
  grey: {
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#eeeeee',
    '300': '#e0e0e0',
    '400': '#bdbdbd',
    '500': '#9e9e9e',
    '600': '#757575',
    '700': '#616161',
    '800': '#424242',
    '900': '#212121',
  },
} as const;

export type CitiboxColors = typeof citiboxColors;
